import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useNotFound } from '../../../../hooks/useNotFound';
import { useGetFilePaths } from '../../../../redux/api/appConfigApi';
import {
  useGetCommunityByIdLazy,
  usePostPutCommunity,
} from '../../../../redux/api/communitiesApi';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import {
  AccessScope,
  FileVisibility,
  PostsInteractionScope,
} from '../../../../shared/constants';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../../utils/files/firebase-storage-utils';
import { getCommunityInfoFormValidation } from './FormikData';

export function useNewCommunityComponent(communityId?: string) {
  const [
    getCommunity,
    { data: community, error, isFetching: isFetchingCommunity },
  ] = useGetCommunityByIdLazy();
  const [postPutPostCommunity] = usePostPutCommunity();
  const { back } = useNavigationStack();
  const { data: filePaths } = useGetFilePaths();
  const { data: user } = useGetMyDetails();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (communityId) getCommunity(communityId);
  }, [communityId]);

  const { t } = useTranslation();
  const [avatar, setAvatar] = useState(null);

  const isOwnerEditingCommunity =
    !!community && !!user?.id && community.ownerId === user.id;
  const ownerAccessConfig =
    isOwnerEditingCommunity && community?.accessConfig
      ? community.accessConfig
      : undefined;

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getCommunityInfoFormValidation(t),
    initialValues: {
      title: community?.title || '',
      description: community?.description || '',
      scope: ownerAccessConfig?.scope ?? AccessScope.public,
      postsScope: ownerAccessConfig?.postsScope ?? PostsInteractionScope.nobody,
      commentsScope:
        ownerAccessConfig?.commentsScope ?? PostsInteractionScope.all,
      avatar: '',
    },
    onSubmit,
  });

  async function onSubmit() {
    if (!filePaths || !user || !user.uid || !user.id) {
      return;
    }

    let avatarFile;

    try {
      setIsLoading(true);

      if (avatar) {
        avatarFile = await uploadFileAndCreateAppFile(
          avatar,
          filePaths.communities,
          user.uid,
          user.id,
          FileVisibility.public,
        );
      }

      await postPutPostCommunity({
        id: communityId || undefined,
        body: {
          title: formik.values.title,
          description: formik.values.description,
          avatar: avatarFile
            ? {
                id: avatarFile.id,
                name: avatarFile.name,
                mimeType: avatarFile.mimeType,
                size: avatarFile.size,
                storageLink: avatarFile.storageLink,
                previewStorageLink: avatarFile.previewStorageLink,
              }
            : undefined,
          accessConfig: {
            scope: formik.values.scope,
            postsScope: formik.values.postsScope,
            commentsScope: formik.values.commentsScope,
          },
        },
      }).unwrap();

      back();
    } catch (error) {
      console.error(
        'Error uploading community avatar or saving community:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
      if (avatarFile) {
        await deleteFilesFromFirebase(
          [avatarFile?.storageLink, avatarFile?.previewStorageLink].filter(
            Boolean,
          ),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  useNotFound(!!error);

  const retryLoadCommunity = useCallback(() => {
    if (communityId) {
      void getCommunity(communityId);
    }
  }, [communityId, getCommunity]);

  return {
    formik,
    avatar,
    setAvatar,
    community,
    communityLoadError: error,
    isLoading,
    isFetchingCommunity,
    retryLoadCommunity,
  };
}
